import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';

const VoiceRecorder = ({ onTranscriptionComplete, onStopRecording }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [transcription, setTranscription] = useState('');
  const [previousTranscription, setPreviousTranscription] = useState('');
  const [salesCoaching, setSalesCoaching] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  // Always using combined real-time + comprehensive approach
  const isRealTimeMode = true;
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [lastProcessedChunk, setLastProcessedChunk] = useState(0);
  const [silenceDetected, setSilenceDetected] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  const chunkProcessingTimeoutRef = useRef(null);

  // Effect to handle recording timer and real-time processing
  useEffect(() => {
    if (isRecording) {
      // Start a timer to track recording duration
      recordingIntervalRef.current = setInterval(() => {
        setRecordingDuration(prev => {
          const newDuration = prev + 1;
          
          // For real-time mode, process at appropriate intervals
          if (isRealTimeMode) {
            // First processing after 30 seconds to ensure we have enough audio
            if (newDuration === 30 && lastProcessedChunk === 0) {
              console.log('Initial processing at 30 seconds');
              processLatestChunk();
            } 
            // Then every 30 seconds thereafter, only process if we haven't processed this chunk yet
            else if (newDuration > 30 && newDuration % 30 === 0 && newDuration > lastProcessedChunk) {
              console.log(`Regular processing at ${newDuration} seconds`);
              processLatestChunk();
            }
          }
          return newDuration;
        });
      }, 1000);
    } else {
      // Clear the timer when not recording
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    }
    
    return () => {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (chunkProcessingTimeoutRef.current) {
        clearTimeout(chunkProcessingTimeoutRef.current);
      }
    };
  }, [isRecording, isRealTimeMode, lastProcessedChunk]);
  
  // Process the latest audio chunk for real-time feedback
  const processLatestChunk = async () => {
    if (audioChunksRef.current.length === 0) return;
    
    // Prevent duplicate processing at the same time
    if (isProcessing) {
      console.log('Already processing, skipping duplicate request');
      return;
    }
    
    try {
      // Mark the current timestamp as being processed to avoid duplicates
      setLastProcessedChunk(recordingDuration);
      console.log(`Processing chunk at ${recordingDuration}s with ${audioChunksRef.current.length} chunks`);
      
      // Create a blob from all chunks collected so far
      // Using all chunks ensures we have the entire speech context
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      console.log('Audio blob size:', audioBlob.size, 'bytes');
      
      // Skip processing if the audio is too small (likely not enough speech)
      if (audioBlob.size < 1000 && recordingDuration > 10) {
        console.log('Audio blob too small, skipping processing');
        return;
      }
      
      // Convert blob to base64
      const reader = new FileReader();
      const base64Audio = await new Promise((resolve, reject) => {
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(audioBlob);
      });
      
      // Get the authorization token
      const token = localStorage.getItem('token');
      
      // Send for processing
      setIsProcessing(true);
      const response = await axios.post('/api/voice/transcribe-buffer', 
        { 
          audio: base64Audio,
          isPartial: true,
          isRealTimeMode: true
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': token
          }
        }
      );
      
      if (response.data.success) {
        console.log('Received transcription:', response.data.transcription);
        setTranscription(response.data.transcription);
        
        // Only update coaching if we received actual coaching content
        if (response.data.salesCoaching && 
            response.data.salesCoaching !== "Recording in progress... Keep speaking for sales coaching feedback.") {
          setSalesCoaching(response.data.salesCoaching);
        }
      }
    } catch (err) {
      console.error('Error processing audio chunk:', err);
      // Don't show errors during real-time processing to avoid disrupting the user
    } finally {
      setIsProcessing(false);
    }
  };

  const startRecording = async () => {
    try {
      // Reset states
      setError('');
      setTranscription('');
      setSalesCoaching('');
      setRecordingDuration(0);
      setLastProcessedChunk(0);
      audioChunksRef.current = [];
      
      // Request audio with higher quality settings for better speech recognition
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100,
          channelCount: 1
        } 
      });
      
      // Try to use the most compatible MIME type
      let mimeType = 'audio/webm';
      
      // Check if the browser supports specific codecs
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      } else if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
        mimeType = 'audio/ogg;codecs=opus';
      }
      
      console.log('Using MIME type:', mimeType);
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: mimeType,
        audioBitsPerSecond: 128000
      });
      mediaRecorderRef.current = mediaRecorder;
      
      // For real-time mode, we need frequent data chunks
      if (isRealTimeMode) {
        // Get data more frequently (500ms) for better real-time processing
        mediaRecorder.start(500);
      } else {
        // Standard mode - get all data at the end
        mediaRecorder.start();
      }
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          console.log('Data chunk received, size:', e.data.size);
          audioChunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(audioBlob);
      };
      
      setIsRecording(true);
    } catch (err) {
      console.error('Error accessing microphone:', err);
      setError('Could not access microphone. Please make sure it is connected and permissions are granted.');
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      // Stop all audio tracks
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      
      // Clear the recording interval
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
      
      // Clear any pending chunk processing
      if (chunkProcessingTimeoutRef.current) {
        clearTimeout(chunkProcessingTimeoutRef.current);
        chunkProcessingTimeoutRef.current = null;
      }
      
      // Notify parent component that recording has stopped (for showing loading state)
      if (onStopRecording) {
        onStopRecording();
      }
      
      // If we were in real-time mode, automatically perform a comprehensive analysis
      if (isRealTimeMode && transcription) {
        console.log('Automatically performing full analysis after Live Feedback session...');
        setTimeout(() => {
          // The full transcription is already in state, use that instead of processing audio again
          generateFullAnalysis(transcription);
        }, 1000);
      }
    }
  };
  
  const transcribeAudio = async () => {
    if (!audioBlob) {
      setError('No audio recorded');
      return;
    }
    
    setIsProcessing(true);
    setError('');
    
    try {
      // Convert blob to base64
      const reader = new FileReader();
      
      // Create a promise to handle the FileReader async operation
      const base64Audio = await new Promise((resolve, reject) => {
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(audioBlob);
      });
      
      console.log('Audio converted to base64, first 50 chars:', base64Audio.substring(0, 50) + '...');
      
      // Get the authorization token
      const token = localStorage.getItem('token');
      
      // Send the base64 data to our new endpoint
      const response = await axios.post('/api/voice/transcribe-buffer', 
        { 
          audio: base64Audio,
          isPartial: false,
          isRealTimeMode: false
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': token
          }
        }
      );
      
      if (response.data.success) {
        setTranscription(response.data.transcription);
        setSalesCoaching(response.data.salesCoaching || '');
        
        if (onTranscriptionComplete) {
          onTranscriptionComplete(response.data.transcription, response.data.salesCoaching);
        }
      } else {
        setError('Analysis failed: ' + (response.data.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error transcribing audio:', err);
      setError(err.response?.data?.message || 'Error transcribing audio');
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Generate a comprehensive analysis from an existing transcription
  const generateFullAnalysis = async (transcriptionText) => {
    try {
      setIsProcessing(true);
      setError('');
      console.log('Generating comprehensive analysis from existing transcription');
      
      // Get the authorization token
      const token = localStorage.getItem('token');
      
      // Send just the transcription for analysis
      const response = await axios.post(
        '/api/voice/analyze-transcript',
        {
          transcription: transcriptionText,
          useFullModel: true
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': token
          }
        }
      );
      
      if (response.data.success) {
        console.log('Received comprehensive analysis');
        setSalesCoaching(response.data.salesCoaching || '');
        
        if (onTranscriptionComplete) {
          onTranscriptionComplete(transcriptionText, response.data.salesCoaching);
        }
      } else {
        setError('Analysis failed: ' + (response.data.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error generating comprehensive analysis:', err);
      setError(err.response?.data?.message || 'Error generating analysis');
    } finally {
      setIsProcessing(false);
    }
  };
  
  const clearRecording = () => {
    setAudioBlob(null);
    setTranscription('');
    setPreviousTranscription('');
    setSalesCoaching('');
    setError('');
    setSilenceDetected(false);
  };

  // Format recording duration into MM:SS
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };
  
  return (
    <div className="p-2 bg-white">
      {/* Removed mode options - using combined approach by default */}
      
      {/* Cowboy emoji with coaching feedback */}
      {isRecording && (
        <div className="flex justify-center mb-2">
          <div className="px-3 py-2 rounded-full bg-purple-50 text-purple-800 text-sm flex items-center gap-2 max-w-sm">
            <span role="img" aria-label="listening" className="text-2xl">🤠</span>
            <span>{salesCoaching ? salesCoaching.split('\n')[0] : "listening..."}</span>
          </div>
        </div>
      )}
    
      <div className="flex justify-center mb-3">
        {!isRecording && !audioBlob && (
          <button 
            className="bg-purple-600 hover:bg-purple-700 text-white py-1.5 px-3 rounded text-sm flex items-center gap-1"
            onClick={startRecording}
            disabled={isProcessing}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" />
            </svg>
            Start AI Sales Coach
          </button>
        )}
        
        {isRecording && (
          <button 
            className="bg-red-600 hover:bg-red-700 text-white py-1.5 px-3 rounded text-sm flex items-center gap-1"
            onClick={stopRecording}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" />
            </svg>
            Stop
          </button>
        )}
        
        {audioBlob && !isRecording && !isRealTimeMode && (
          <div className="flex gap-2">
            <button 
              className="bg-blue-600 hover:bg-blue-700 text-white py-1.5 px-3 rounded text-sm flex items-center gap-1"
              onClick={transcribeAudio}
              disabled={isProcessing}
            >
              Analyze
            </button>
            <button 
              className="bg-gray-500 hover:bg-gray-600 text-white py-1.5 px-3 rounded text-sm"
              onClick={clearRecording}
              disabled={isProcessing}
            >
              Clear
            </button>
          </div>
        )}
        
        {audioBlob && !isRecording && isRealTimeMode && (
          <button 
            className="bg-gray-500 hover:bg-gray-600 text-white py-1.5 px-3 rounded text-sm"
            onClick={clearRecording}
            disabled={isProcessing}
          >
            Record Again
          </button>
        )}
      </div>
      
      {isProcessing && !isRealTimeMode && (
        <div className="flex items-center justify-center my-2">
          <div className="w-4 h-4 border border-t-blue-500 rounded-full animate-spin mr-2"></div>
          <span className="text-gray-600 text-sm">Processing...</span>
        </div>
      )}
      
      {error && (
        <div className="text-red-500 my-2 text-sm text-center">
          {error}
        </div>
      )}
      
      
      {/* Only show transcript if not in real-time mode or if not currently recording */}
      {transcription && (!isRealTimeMode || !isRecording) && (
        <div className="mt-2 border-l-2 border-blue-400 pl-2">
          <div className="text-xs text-blue-600 mb-1">Transcription:</div>
          <div className="whitespace-pre-wrap text-sm text-gray-700">
            <p>{transcription}</p>
          </div>
        </div>
      )}
      
      {/* Removed the "Coaching updates every 30 seconds" message */}
      
      {/* Feedback now appears next to the cowboy emoji during recording */}
      {salesCoaching && !isRecording && !isRealTimeMode && (
        <div className="mt-3 border-l-2 border-purple-500 pl-2">
          <div className="text-xs text-purple-600 mb-1">Coaching Feedback:</div>
          <div className="whitespace-pre-wrap text-sm text-gray-700">
            {salesCoaching.split('\n').map((paragraph, index) => (
              paragraph.trim() ? (
                <p key={index} className="mb-1.5">{paragraph}</p>
              ) : null
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceRecorder;